import localizeHref from "../../../lib/multilingual/localizeHref";
import abaAwardImage from "../../assets/footer/aba-award.png";
import cioAwardImage from "../../assets/footer/cio-award.png";
import kpmgAwardImage from "../../assets/footer/kpmg-award.png";
import mtAwardImage from "../../assets/footer/mt-award.png";
import { LinkEntry, LinkPath } from "@/common/contracts/footer.contracts";
import {
  ABOUT_ROUTE,
  ACCOUNT_ADS_ROUTE,
  ACCOUNT_FAVORITES_ROUTE,
  ACCOUNT_PURCHASES_ROUTE,
  ACCOUNT_SALES_ROUTE,
  ACCOUNT_SEARCHES_ROUTE,
  ACCOUNT_SETTINGS_ROUTE,
  ART_ROUTE,
  BRANDS_ROUTE,
  BUSINESS_TO_BUSINESS_ROUTE,
  CHAT_ROUTE,
  COFFEE_TABLE_BOOKS_ROUTE,
  COLLECTIONS_ROUTE,
  CONTACT_US_ROUTE,
  CURATION_ROUTE,
  DECORATION_ROUTE,
  FURNITURE_ROUTE,
  HELP_ROUTE,
  HIW_BUYING_ROUTE,
  HIW_SELLING_ROUTE,
  IMPRESSUM_ROUTE,
  LIGHTING_ROUTE,
  PAYMENT_ROUTE,
  PRIVACY_ROUTE,
  REVIEWS_ROUTE,
  SHIPPING_ROUTE,
  TERMS_ROUTE,
} from "@/common/static/routes.static";
import { PRO_BUYER_PROGRAM_ENABLED } from "@/config";
import {
  PRO_BUYER_NAV_LABEL,
  PRO_BUYER_ROUTE,
} from "@/features/pro-buyer-application/static/pro-buyer-application.static";
import { type ContentLocale, configuredLocales, locales } from "@/i18n/config";

/**
 * The Impressum is a German legal requirement, so the link shows on `de` only.
 * Derived from the routed locales rather than written out, so a new routed
 * locale (`en-us`, WHO-5766) does not silently start showing it.
 */
const EVERY_LOCALE_EXCEPT_DE = locales.filter(locale => locale !== "de");

/**
 * Help-center article "What is Whoppah's return policy?" per locale. Article
 * slugs are translated in the help service, so the footer needs one path per
 * locale (the `LinkPath` shape). Keep in sync with the help admin if a slug
 * changes; the help service redirects old Elevio ids, not renamed slugs.
 */
export const RETURN_POLICY_ROUTES: LinkPath = {
  en: `${HELP_ROUTE}/return/what-is-whoppahs-return-policy`,
  nl: `${HELP_ROUTE}/retourneren-2/wat-is-het-retourbeleid-van-whoppah`,
  de: `${HELP_ROUTE}/ruckgabe-2/wie-lautet-die-ruckgaberichtlinie-von-whoppah`,
  fr: `${HELP_ROUTE}/retours-2/quelle-est-la-politique-de-retour-de-whoppah`,
  es: `${HELP_ROUTE}/devolucion-2/cual-es-la-politica-de-devolucion-de-whoppah`,
  it: `${HELP_ROUTE}/ritorno/qual-e-la-politica-di-reso-di-whoppah`,
};

export const INSTAGRAM_URL = "https://www.instagram.com/whoppah";
export const LINKEDIN_URL = "https://www.linkedin.com/company/whoppah/";
export const FACEBOOK_URL = "https://www.facebook.com/WhoppahSocial";
export const TIKTOK_URL = "https://www.tiktok.com/@whoppahsocial";

export const AWARDS = [
  { title: "cio", image: cioAwardImage },
  { title: "aba", image: abaAwardImage },
  { title: "kmpg", image: kpmgAwardImage },
  { title: "mt-sprout", image: mtAwardImage },
];

type LinkValue = {
  title: string;
  links: LinkEntry[];
};

export const LINKS_MAP: LinkValue[] = [
  {
    title: "footer:footer-user",
    links: [
      {
        label: "footer:footer-my-account",
        path: ACCOUNT_SETTINGS_ROUTE,
      },
      {
        label: "footer:footer-user-sales",
        path: ACCOUNT_SALES_ROUTE,
      },
      {
        label: "footer:footer-user-purchases",
        path: ACCOUNT_PURCHASES_ROUTE,
      },
      {
        label: "footer:footer-your-ads",
        path: ACCOUNT_ADS_ROUTE,
      },
      {
        label: "footer:footer-saved-searches",
        path: ACCOUNT_SEARCHES_ROUTE,
      },
      {
        label: "footer:footer-chats",
        path: CHAT_ROUTE,
      },
      {
        label: "footer:footer-favorites",
        path: ACCOUNT_FAVORITES_ROUTE,
      },
    ],
  },
  {
    title: "footer:footer-categories",
    links: [
      {
        label: "footer:footer-category-furniture",
        path: FURNITURE_ROUTE,
      },
      {
        label: "footer:footer-category-lighting",
        path: LIGHTING_ROUTE,
      },
      {
        label: "footer:footer-category-art",
        path: ART_ROUTE,
      },
      {
        label: "footer:footer-category-decoration",
        path: DECORATION_ROUTE,
      },
      {
        label: "footer:footer-category-coffee-books",
        path: COFFEE_TABLE_BOOKS_ROUTE,
      },
      {
        label: "footer:footer-category-brands",
        path: configuredLocales.reduce(
          (acc, locale) => {
            acc[locale] = localizeHref(BRANDS_ROUTE, "en", locale);
            return acc;
          },
          {} as Record<ContentLocale, string>
        ),
      },
      {
        label: "footer:footer-category-collections",
        path: COLLECTIONS_ROUTE,
      },
    ],
  },
  {
    title: "footer:footer-buy-sell",
    links: [
      {
        label: "footer:footer-how-it-works-selling",
        path: HIW_SELLING_ROUTE,
      },
      {
        label: "footer:footer-how-it-works-buying",
        path: HIW_BUYING_ROUTE,
      },
      {
        label: "footer:footer-business-to-business",
        path: BUSINESS_TO_BUSINESS_ROUTE,
      },
      // WHO-5688: next to Whoppah zakelijk, only while the program is on.
      ...(PRO_BUYER_PROGRAM_ENABLED
        ? [
            {
              label: PRO_BUYER_NAV_LABEL,
              path: PRO_BUYER_ROUTE,
              rawLabel: true,
            },
          ]
        : []),
      {
        label: "footer:footer-curation-rules",
        path: CURATION_ROUTE,
      },

      {
        label: "footer:footer-payment",
        path: PAYMENT_ROUTE,
      },
      {
        label: "footer:footer-shipping",
        path: SHIPPING_ROUTE,
      },

      {
        label: "footer:footer-bidding",
        path: HIW_BUYING_ROUTE,
      },
    ],
  },
  {
    title: "footer:footer-whoppah",
    links: [
      {
        label: "footer:footer-about-us",
        path: ABOUT_ROUTE,
      },
      {
        label: "footer:footer-reviews",
        path: REVIEWS_ROUTE,
      },
      {
        label: "footer:footer-faq",
        path: HELP_ROUTE,
      },
      {
        label: "footer:footer-contact-us",
        path: CONTACT_US_ROUTE,
      },
      {
        // One help article per locale; slugs are translated, so no shared path.
        // Pinterest and Google merchant reviews require a return policy that is
        // reachable from every page, hence the footer link.
        label: "footer:footer-return-policy",
        path: RETURN_POLICY_ROUTES,
      },
      {
        label: "footer:footer-privacy-and-cookies",
        path: PRIVACY_ROUTE,
        externalLink: true,
      },
      {
        label: "footer:footer-terms-and-conditions",
        path: TERMS_ROUTE,
        externalLink: true,
      },
      {
        label: "footer:footer-impressum",
        path: IMPRESSUM_ROUTE,
        externalLink: true,
        excludeOnLocals: EVERY_LOCALE_EXCEPT_DE,
      },
    ],
  },
];
